/**
 * Streaming PCM16 playback for voice sessions.
 *
 * Server `voice_audio` chunks (base64 PCM16 mono @ 24 kHz) are wrapped in
 * `AudioBuffer`s and schedule-queued back-to-back into the shared audio-theme
 * graph (`getAudioGraph()` → `masterGain`), so the user's volume setting
 * applies and the mic-permission click has already satisfied the autoplay
 * gesture. `flush()` (on `voice_interrupted` / session end) stops every
 * scheduled source and resets the queue.
 */

import { getAudioGraph } from '../utils/audio-theme';

const OUTPUT_SAMPLE_RATE = 24_000;
/** Small scheduling headroom so a chunk arriving "now" doesn't start in the past. */
const SCHEDULE_HEADROOM_S = 0.02;

export interface PcmPlayer {
  /** Queue one base64 PCM16 chunk for gapless playback. */
  enqueue: (chunkB64: string) => void;
  /** Stop everything scheduled and reset the queue (barge-in / teardown). */
  flush: () => void;
  /** True while queued audio extends past the current playback head — drives
   *  the session bar's "agent is speaking" state without extra events. */
  isSpeaking: () => boolean;
}

/** Minimal slice of the Web Audio API the player touches — lets unit tests
 *  inject a fake graph (jsdom has no AudioContext). */
export interface PcmAudioGraph {
  ctx: Pick<AudioContext, 'createBuffer' | 'createBufferSource' | 'currentTime' | 'state' | 'resume'>;
  masterGain: AudioNode;
}

function fromBase64(b64: string): Int16Array | null {
  try {
    const bin = atob(b64);
    const sampleCount = Math.floor(bin.length / 2);
    if (sampleCount === 0) return null;
    const bytes = new Uint8Array(sampleCount * 2);
    for (let i = 0; i < bytes.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Int16Array(bytes.buffer);
  } catch {
    return null;
  }
}

export function createPcmPlayer(
  rate: number = OUTPUT_SAMPLE_RATE,
  graph: PcmAudioGraph | null = getAudioGraph(),
): PcmPlayer | null {
  if (!graph) return null;
  const { ctx, masterGain } = graph;
  const scheduled = new Set<AudioBufferSourceNode>();
  let nextStartTime = 0;

  return {
    enqueue: (chunkB64: string) => {
      const samples = fromBase64(chunkB64);
      if (!samples) return;
      if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
      const buffer = ctx.createBuffer(1, samples.length, rate);
      const channel = buffer.getChannelData(0);
      for (let i = 0; i < samples.length; i++) channel[i] = samples[i] / 0x8000;
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(masterGain);
      src.onended = () => scheduled.delete(src);
      scheduled.add(src);
      const startAt = Math.max(ctx.currentTime + SCHEDULE_HEADROOM_S, nextStartTime);
      try {
        src.start(startAt);
      } catch {
        scheduled.delete(src);
        return;
      }
      nextStartTime = startAt + buffer.duration;
    },
    flush: () => {
      for (const src of scheduled) {
        try {
          src.stop();
        } catch {
          /* not started / already stopped */
        }
      }
      scheduled.clear();
      nextStartTime = 0;
    },
    isSpeaking: () => scheduled.size > 0 && nextStartTime > ctx.currentTime,
  };
}
